import { useRef, useSyncExternalStore } from 'react';
import { computeFitScale } from '../computeFitScale';

export interface UseViewportFitScaleOptions {
  /** Disable (e.g. on mobile, where the panel reflows by width instead). */
  enabled?: boolean;
  /** Readability floor — element never shrinks below this. */
  minScale?: number;
  /** Fraction of each viewport axis the frame targets (breathing room = 1 − fraction). */
  fraction?: number;
  /** Upper bound — allows upscaling past design size on roomy viewports. */
  maxScale?: number;
  /** Design (untransformed) width of the fixed-geometry frame, px. */
  designWidth?: number;
  /**
   * Design (untransformed) height of the ZOOMED element — the whole auth column,
   * px. = panel natural height + above-panel slot (NeuralPath ~90) + gap 12. Must
   * cover the entire zoomed column, not just the panel, otherwise the column
   * overflows the viewport (zoom targets the column, so the design height must be
   * the column's). Threaded as a DECLARED CONSTANT per step/flow (default = tallest
   * step) — never a live DOM measurement, to keep computeFitScale non-circular.
   */
  designHeight?: number;
}

/**
 * Continuously scales a fixed-geometry frame to fit the viewport.
 *
 * Scale is computed against the FIXED design size (`designWidth`/`designHeight`,
 * a declared-constant column size) — never against a live DOM measurement. This keeps the
 * computation non-circular and content-independent: navigating between step-form
 * steps changes content height, but the design size is a constant, so the scale
 * does not chase it. The ResizeObserver below only re-runs the recompute; it
 * does NOT feed the observed size back into the scale.
 *
 * Apply the returned `scale` as `zoom` (NOT `transform: scale()`) on the frame.
 * `zoom` shrinks the element's actual LAYOUT FOOTPRINT, whereas `transform` is
 * visual-only and leaves the natural (unscaled) box in flow. With `transform`,
 * a tall step could push the page taller than the viewport, raise a body
 * scrollbar, shave ~15px off the available width, and feed that back into the
 * next recompute — a scrollbar↔scale jitter loop. `zoom` removes that footprint
 * so no scrollbar forms and the feedback loop dies.
 *
 * Available size is read from `visualViewport` or `documentElement.clientWidth/
 * clientHeight` (which already exclude any scrollbar gutter), times `fraction`
 * on each axis.
 *
 * Returns `{ ref, scale }`; attach `ref` to the un-zoomed scene element so the
 * observed content (the zoomed frame inside it) drives content-aware refits.
 * Reads the viewport size through useSyncExternalStore, so it is current on
 * the first paint and on window/visualViewport/element resize.
 */
/** 스냅샷은 문자열이어야 같은 크기에서 다시 그리지 않는다. */
function readViewportSize(): string {
  const viewport = window.visualViewport;
  const root = document.documentElement;
  return `${viewport?.width ?? root.clientWidth}x${viewport?.height ?? root.clientHeight}`;
}

export function useViewportFitScale<T extends HTMLElement>({
  enabled = true,
  minScale = 0.6,
  maxScale = 1.6,
  fraction = 0.92,
  designWidth = 480,
  designHeight = 940,
}: UseViewportFitScaleOptions = {}) {
  const ref = useRef<T>(null);

  // 뷰포트 크기를 외부 저장소로 구독한다 — 렌더 중에 읽으므로 첫 페인트부터 맞는 배율이다.
  // subscribe 의 안정성은 React Compiler 가 enabled 기준으로 메모해 지킨다.
  const subscribe = (onChange: () => void) => {
    if (!enabled) return () => {};
    window.addEventListener('resize', onChange);
    window.visualViewport?.addEventListener('resize', onChange);

    const el = ref.current;
    const observer = el ? new ResizeObserver(onChange) : null;
    if (el && observer) observer.observe(el);

    return () => {
      window.removeEventListener('resize', onChange);
      window.visualViewport?.removeEventListener('resize', onChange);
      observer?.disconnect();
    };
  };
  const viewportSize = useSyncExternalStore(subscribe, readViewportSize);

  let scale = 1;
  if (enabled) {
    const [width, height] = viewportSize.split('x').map(Number) as [number, number];
    scale = computeFitScale({
      naturalWidth: designWidth,
      naturalHeight: designHeight,
      availableWidth: width * fraction,
      availableHeight: height * fraction,
      minScale,
      maxScale,
    });
  }

  return { ref, scale };
}
